/**
 * AYU Hörbuch — "Der AYU – Tradition trifft Genetik"
 * 
 * 13 Kapitel, 3 Kapitel gratis als Vorschau (Vorwort + Kap 1 + Kap 2).
 * Kauf über Stripe (9,90 €) schaltet alle Kapitel frei.
 * Premium-Mitglieder mit Admin-Grant haben automatisch Zugang.
 */
import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useClientAuth } from "@/contexts/ClientAuthContext";
import { useDemo } from "@/contexts/DemoContext";
import { useI18n } from "@/contexts/I18nContext";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { useAudioCache } from "@/hooks/useAudioCache";
import {
  BookAudio,
  Play,
  Pause,
  Lock,
  Crown,
  Clock,
  ChevronDown,
  ChevronUp,
  Volume2,
  VolumeX,
  SkipBack,
  SkipForward,
  Check,
  ShoppingCart,
  Loader2,
  BookOpen,
  Sparkles,
  RotateCcw,
  X,
  Bookmark,
  BookmarkCheck,
  Trash2,
  Download,
  Wifi,
  WifiOff,
  Timer,
  Moon,
  StickyNote,
  Pencil,
  MessageSquare,
  FileDown,
  Brain,
  BarChart3,
  Award,
  Lightbulb,
} from "lucide-react";

/* ═══════════════════════════════════════════════════════════
   Chapter Data
   ═══════════════════════════════════════════════════════════ */
interface Chapter {
  id: string;
  title: string;
  chapter: number;
  durationMin: number;
  description: string;
  audioUrl: string;
  isFree: boolean;
}

const CHAPTERS: Chapter[] = [
  {
    id: "vorwort",
    title: "Vorwort",
    chapter: 0,
    durationMin: 5,
    description: "Annam und Christopher Hanfstein über die Entstehung von AYU und die Verbindung von Tradition und moderner Wissenschaft.",
    audioUrl: "https://d2xsxph8kpxj0f.cloudfront.net/310519663397508885/Atzyym4VHHtDL2VhJSwxBc/vorwort_3d10334d.mp3",
    isFree: true,
  },
  {
    id: "kapitel-01",
    title: "Kapitel 1 – Einleitung",
    chapter: 1,
    durationMin: 6,
    description: "Warum Ayurveda und Genetik zusammengehören — und wie AYU einen neuen Weg in der Gesundheitsvorsorge beschreitet.",
    audioUrl: "https://d2xsxph8kpxj0f.cloudfront.net/310519663397508885/Atzyym4VHHtDL2VhJSwxBc/kapitel-01_2f0c9ab8.mp3",
    isFree: true,
  },
  {
    id: "kapitel-02",
    title: "Kapitel 2 – Ayurveda in Indien",
    chapter: 2,
    durationMin: 3,
    description: "Die Wurzeln des Ayurveda auf dem indischen Subkontinent und seine Jahrtausende alte Heiltradition.",
    audioUrl: "https://d2xsxph8kpxj0f.cloudfront.net/310519663397508885/Atzyym4VHHtDL2VhJSwxBc/kapitel-02_1b1c70fd.mp3",
    isFree: true,
  },
  {
    id: "kapitel-03",
    title: "Kapitel 3 – Heiltradition und Prävention",
    chapter: 3,
    durationMin: 3,
    description: "Wie Ayurveda seit Jahrhunderten Prävention vor Heilung stellt — und was wir daraus lernen können.",
    audioUrl: "https://d2xsxph8kpxj0f.cloudfront.net/310519663397508885/Atzyym4VHHtDL2VhJSwxBc/kapitel-03_89599806.mp3",
    isFree: false,
  },
  {
    id: "kapitel-04",
    title: "Kapitel 4 – Grenzen der Übertragbarkeit",
    chapter: 4,
    durationMin: 7,
    description: "Warum indische Ayurveda-Empfehlungen nicht 1:1 auf den europäischen Kontext übertragbar sind.",
    audioUrl: "https://d2xsxph8kpxj0f.cloudfront.net/310519663397508885/Atzyym4VHHtDL2VhJSwxBc/kapitel-04_9c608cf2.mp3",
    isFree: false,
  },
  {
    id: "kapitel-05",
    title: "Kapitel 5 – Genetische Unterschiede",
    chapter: 5,
    durationMin: 7,
    description: "Internationale genetische Unterschiede und warum ein personalisierter Ansatz unverzichtbar ist.",
    audioUrl: "https://d2xsxph8kpxj0f.cloudfront.net/310519663397508885/Atzyym4VHHtDL2VhJSwxBc/kapitel-05_e566061f.mp3",
    isFree: false,
  },
  {
    id: "kapitel-06",
    title: "Kapitel 6 – Klima & Geografie",
    chapter: 6,
    durationMin: 7,
    description: "Der Einfluss von Klima, Geografie und Umwelt auf unsere Konstitution und Gesundheit.",
    audioUrl: "https://d2xsxph8kpxj0f.cloudfront.net/310519663397508885/Atzyym4VHHtDL2VhJSwxBc/kapitel-06_03a8826a.mp3",
    isFree: false,
  },
  {
    id: "kapitel-07",
    title: "Kapitel 7 – Der AYU-Ansatz",
    chapter: 7,
    durationMin: 6,
    description: "Wie AYU Ayurveda mit moderner Genetik und Epigenetik zu einem ganzheitlichen System verbindet.",
    audioUrl: "https://d2xsxph8kpxj0f.cloudfront.net/310519663397508885/Atzyym4VHHtDL2VhJSwxBc/kapitel-07_240e1d59.mp3",
    isFree: false,
  },
  {
    id: "kapitel-08",
    title: "Kapitel 8 – Die drei AYU-Fragebögen",
    chapter: 8,
    durationMin: 8,
    description: "Prakriti, Vikriti und Lifestyle — die drei Säulen der AYU-Phänotyp-Bestimmung im Detail.",
    audioUrl: "https://d2xsxph8kpxj0f.cloudfront.net/310519663397508885/Atzyym4VHHtDL2VhJSwxBc/kapitel-08_fd95d55e.mp3",
    isFree: false,
  },
  {
    id: "kapitel-09",
    title: "Kapitel 9 – Praktische Anwendung",
    chapter: 9,
    durationMin: 7,
    description: "Konkrete Tipps und Anleitungen, wie du AYU-Erkenntnisse in deinen Alltag integrierst.",
    audioUrl: "https://d2xsxph8kpxj0f.cloudfront.net/310519663397508885/Atzyym4VHHtDL2VhJSwxBc/kapitel-09_861e48de.mp3",
    isFree: false,
  },
  {
    id: "kapitel-10",
    title: "Kapitel 10 – Empfehlungen",
    chapter: 10,
    durationMin: 7,
    description: "Personalisierte Empfehlungen für Ernährung, Bewegung und Lebensstil basierend auf deinem Phänotyp.",
    audioUrl: "https://d2xsxph8kpxj0f.cloudfront.net/310519663397508885/Atzyym4VHHtDL2VhJSwxBc/kapitel-10_31c18425.mp3",
    isFree: false,
  },
  {
    id: "kapitel-11",
    title: "Kapitel 11 – Langfristige Prävention & Zukunftsmedizin",
    chapter: 11,
    durationMin: 9,
    description: "Die Zukunft der personalisierten Medizin und wie AYU dazu beiträgt, Krankheiten vorzubeugen.",
    audioUrl: "https://d2xsxph8kpxj0f.cloudfront.net/310519663397508885/Atzyym4VHHtDL2VhJSwxBc/kapitel-11_78843872.mp3",
    isFree: false,
  },
  {
    id: "nachwort",
    title: "Nachwort",
    chapter: 12,
    durationMin: 3,
    description: "Ein persönlicher Abschluss der Autoren — und ein Ausblick auf die Reise mit AYU.",
    audioUrl: "https://d2xsxph8kpxj0f.cloudfront.net/310519663397508885/Atzyym4VHHtDL2VhJSwxBc/nachwort_beba1b26.mp3",
    isFree: false,
  },
];

const TOTAL_DURATION = CHAPTERS.reduce((sum, c) => sum + c.durationMin, 0);

/* ═══════════════════════════════════════════════════════════
   Format helpers
   ═══════════════════════════════════════════════════════════ */
function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

/* ═══════════════════════════════════════════════════════════
   Playback state shared between player, chapter list & mini-player
   ═══════════════════════════════════════════════════════════ */
interface PlaybackState {
  isPlaying: boolean;
  currentTime: number;
  duration: number;
}

/* ═══════════════════════════════════════════════════════════
   Audio Player Component
   ═══════════════════════════════════════════════════════════ */
function AudiobookPlayer({
  chapter,
  onNext,
  onPrev,
  hasNext,
  hasPrev,
  autoPlay = false,
  onAutoPlayHandled,
  onPlaybackChange,
}: {
  chapter: Chapter;
  onNext: () => void;
  onPrev: () => void;
  hasNext: boolean;
  hasPrev: boolean;
  autoPlay?: boolean;
  onAutoPlayHandled?: () => void;
  onPlaybackChange?: (state: PlaybackState) => void;
}) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [isLoading, setIsLoading] = useState(true);

  // Notify parent of playback state changes
  useEffect(() => {
    onPlaybackChange?.({ isPlaying, currentTime, duration });
  }, [isPlaying, currentTime, duration]);

  // Reset on chapter change
  useEffect(() => {
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    setIsLoading(true);
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  }, [chapter.id]);

  // Auto-play when triggered from chapter list play button
  useEffect(() => {
    if (autoPlay && audioRef.current) {
      const tryPlay = () => {
        audioRef.current?.play().then(() => {
          setIsPlaying(true);
        }).catch(() => {});
        onAutoPlayHandled?.();
      };
      // If audio is already loaded, play immediately; otherwise wait for canplay
      if (audioRef.current.readyState >= 2) {
        tryPlay();
      } else {
        const handler = () => { tryPlay(); audioRef.current?.removeEventListener("canplay", handler); };
        audioRef.current.addEventListener("canplay", handler);
        return () => { audioRef.current?.removeEventListener("canplay", handler); };
      }
    }
  }, [autoPlay, chapter.id]);

  // Listen for external toggle-play events (from mini-player or chapter list pause button)
  useEffect(() => {
    const handler = () => {
      if (!audioRef.current) return;
      if (audioRef.current.paused) {
        audioRef.current.play().catch(() => {});
        setIsPlaying(true);
      } else {
        audioRef.current.pause();
        setIsPlaying(false);
      }
    };
    window.addEventListener("ayu-toggle-play", handler);
    return () => window.removeEventListener("ayu-toggle-play", handler);
  }, []);

  // Listen for seek-to events (from bookmarks)
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (audioRef.current && detail?.timeSec !== undefined) {
        audioRef.current.currentTime = detail.timeSec;
        setCurrentTime(detail.timeSec);
        if (audioRef.current.paused) {
          audioRef.current.play().catch(() => {});
          setIsPlaying(true);
        }
      }
    };
    window.addEventListener("ayu-seek-to", handler);
    return () => window.removeEventListener("ayu-seek-to", handler);
  }, []);

  // Listen for speed changes from mini-player
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (audioRef.current && detail?.rate !== undefined) {
        audioRef.current.playbackRate = detail.rate;
        setPlaybackRate(detail.rate);
      }
    };
    window.addEventListener("ayu-set-speed", handler);
    return () => window.removeEventListener("ayu-set-speed", handler);
  }, []);

  // Listen for sleep timer stop event
  useEffect(() => {
    const handler = () => {
      if (audioRef.current && !audioRef.current.paused) {
        audioRef.current.pause();
        setIsPlaying(false);
      }
    };
    window.addEventListener("ayu-sleep-timer-stop", handler);
    return () => window.removeEventListener("ayu-sleep-timer-stop", handler);
  }, []);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.playbackRate = playbackRate;
    }
  }, [playbackRate]);

  const togglePlay = useCallback(() => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play().catch(() => {});
    }
    setIsPlaying(!isPlaying);
  }, [isPlaying]);

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
      setIsLoading(false);
    }
  };

  const handleEnded = () => {
    setIsPlaying(false);
    if (hasNext) {
      onNext();
    }
  };

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!progressRef.current || !audioRef.current || !duration) return;
    const rect = progressRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const pct = x / rect.width;
    audioRef.current.currentTime = pct * duration;
    setCurrentTime(pct * duration);
  };

  const skip = (seconds: number) => {
    if (!audioRef.current) return;
    audioRef.current.currentTime = Math.max(0, Math.min(duration, audioRef.current.currentTime + seconds));
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div className="bg-gradient-to-br from-olive-dark/95 to-midnight/95 rounded-2xl p-5 sm:p-6 text-ivory shadow-xl">
      <audio
        ref={audioRef}
        src={chapter.audioUrl}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={handleEnded}
        onCanPlay={() => setIsLoading(false)}
        muted={isMuted}
        preload="metadata"
      />

      {/* Chapter Info */}
      <div className="flex items-start gap-3 mb-5">
        <div className="w-12 h-12 rounded-xl bg-gold/20 flex items-center justify-center shrink-0">
          <BookAudio className="w-6 h-6 text-gold" />
        </div>
        <div className="min-w-0">
          <p className="text-xs text-gold font-medium tracking-wider uppercase">Jetzt hören</p>
          <h3 className="text-lg font-display font-semibold text-ivory truncate">{chapter.title}</h3>
        </div>
      </div>

      {/* Progress Bar */}
      <div
        ref={progressRef}
        className="h-2 bg-ivory/15 rounded-full cursor-pointer mb-2 group"
        onClick={handleProgressClick}
      >
        <div
          className="h-full bg-gradient-to-r from-gold to-gold-light rounded-full relative transition-all duration-150"
          style={{ width: `${progress}%` }}
        >
          <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3.5 h-3.5 bg-gold rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
      </div>

      {/* Time */}
      <div className="flex justify-between text-xs text-ivory/60 mb-4 font-body">
        <span>{formatTime(currentTime)}</span>
        <span>{duration > 0 ? formatTime(duration) : `~${chapter.durationMin} Min`}</span>
      </div>

      {/* Controls */}
      <div className="flex items-center justify-center gap-3 sm:gap-4">
        {/* Mute */}
        <button
          onClick={() => setIsMuted(!isMuted)}
          className="w-9 h-9 rounded-full flex items-center justify-center text-ivory/60 hover:text-ivory hover:bg-ivory/10 transition-all"
        >
          {isMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
        </button>

        {/* Prev */}
        <button
          onClick={hasPrev ? onPrev : () => skip(-15)}
          className="w-10 h-10 rounded-full flex items-center justify-center text-ivory/70 hover:text-ivory hover:bg-ivory/10 transition-all"
          title={hasPrev ? "Vorheriges Kapitel" : "15s zurück"}
        >
          {hasPrev ? <SkipBack className="w-5 h-5" /> : <RotateCcw className="w-4 h-4" />}
        </button>

        {/* Play/Pause */}
        <button
          onClick={togglePlay}
          disabled={isLoading}
          className="w-14 h-14 rounded-full bg-gold text-midnight flex items-center justify-center hover:bg-gold-light transition-all shadow-lg hover:shadow-xl active:scale-95 disabled:opacity-50"
        >
          {isLoading ? (
            <Loader2 className="w-6 h-6 animate-spin" />
          ) : isPlaying ? (
            <Pause className="w-6 h-6" />
          ) : (
            <Play className="w-6 h-6 ml-0.5" />
          )}
        </button>

        {/* Next */}
        <button
          onClick={hasNext ? onNext : () => skip(15)}
          className="w-10 h-10 rounded-full flex items-center justify-center text-ivory/70 hover:text-ivory hover:bg-ivory/10 transition-all"
          title={hasNext ? "Nächstes Kapitel" : "15s vor"}
        >
          {hasNext ? <SkipForward className="w-5 h-5" /> : <span className="text-xs font-medium">+15</span>}
        </button>

        {/* Speed */}
        <button
          onClick={() => setPlaybackRate((r) => (r >= 2 ? 0.75 : r + 0.25))}
          className="w-9 h-9 rounded-full flex items-center justify-center text-ivory/60 hover:text-ivory hover:bg-ivory/10 transition-all text-xs font-bold"
        >
          {playbackRate}x
        </button>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   Chapter List Item — with progress bar & play/pause toggle
   ═══════════════════════════════════════════════════════════ */
function ChapterItem({
  chapter,
  isActive,
  isLocked,
  isCurrentlyPlaying,
  progressPercent,
  isOffline,
  onSelect,
  onPlay,
  onPause,
}: {
  chapter: Chapter;
  isActive: boolean;
  isLocked: boolean;
  isCurrentlyPlaying: boolean;
  progressPercent: number;
  isOffline: boolean;
  onSelect: () => void;
  onPlay: () => void;
  onPause: () => void;
}) {
  const { t } = useI18n();
  return (
    <div
      className={`relative w-full text-left rounded-xl border transition-all group overflow-hidden ${
        isActive
          ? "bg-olive/10 border-olive/30 shadow-sm"
          : isLocked
          ? "bg-muted/30 border-transparent opacity-60"
          : "bg-card border-border/50 hover:border-olive/20 hover:shadow-sm"
      }`}
    >
      {/* Progress bar at bottom of card */}
      {!isLocked && progressPercent > 0 && (
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-olive/10">
          <div
            className="h-full bg-olive/40 rounded-r-full transition-all duration-300"
            style={{ width: `${Math.min(progressPercent, 100)}%` }}
          />
        </div>
      )}

      <button
        onClick={isLocked ? undefined : onSelect}
        disabled={isLocked}
        className={`w-full text-left p-4 ${!isLocked ? "cursor-pointer" : "cursor-not-allowed"}`}
      >
        <div className="flex items-start gap-3">
          {/* Chapter Number */}
          <div
            className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 text-sm font-bold ${
              isActive
                ? "bg-olive text-ivory"
                : isLocked
                ? "bg-muted text-muted-foreground"
                : "bg-olive/10 text-olive group-hover:bg-olive/20"
            }`}
          >
            {isLocked ? <Lock className="w-3.5 h-3.5" /> : chapter.chapter === 0 ? "V" : chapter.chapter}
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h4 className={`text-sm font-semibold truncate ${isActive ? "text-olive-dark" : "text-foreground"}`}>
                {chapter.title}
              </h4>
              {chapter.isFree && (
                <span className="text-[10px] font-bold uppercase tracking-wider text-gold bg-gold/10 px-1.5 py-0.5 rounded-full shrink-0">
                  {t("hoerbuch.gratis")}
                </span>
              )}
              {isActive && (
                <span className="text-[10px] font-bold uppercase tracking-wider text-olive bg-olive/10 px-1.5 py-0.5 rounded-full shrink-0">
                  {t("hoerbuch.aktiv")}
                </span>
              )}
              {isOffline && !isLocked && (
                <span className="text-[10px] font-medium text-muted-foreground flex items-center gap-0.5 shrink-0" title={t("hoerbuch.offlineVerfuegbar")}>
                  <Download className="w-2.5 h-2.5" />
                </span>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{chapter.description}</p>
            <div className="flex items-center gap-3 mt-1.5 text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                ~{chapter.durationMin} Min
              </span>
              {/* Show listened progress text */}
              {!isLocked && progressPercent > 0 && progressPercent < 100 && (
                <span className="text-olive font-medium">{Math.round(progressPercent)}% {t("hoerbuch.gehoert")}</span>
              )}
              {!isLocked && progressPercent >= 100 && (
                <span className="flex items-center gap-0.5 text-olive font-medium">
                  <Check className="w-3 h-3" /> {t("hoerbuch.gehoertLabel")}
                </span>
              )}
            </div>
          </div>

          {/* Play/Pause button — always visible on mobile, hover on desktop */}
          {!isLocked && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                if (isActive && isCurrentlyPlaying) {
                  onPause();
                } else {
                  onPlay();
                }
              }}
              className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 self-center ${
                isActive
                  ? "bg-olive text-ivory shadow-md hover:bg-olive-dark"
                  : "bg-olive/10 text-olive sm:opacity-0 sm:group-hover:opacity-100 hover:bg-olive/20"
              } transition-all active:scale-90`}
              aria-label={isActive && isCurrentlyPlaying ? t("hoerbuch.kapitelPausieren", { title: chapter.title }) : t("hoerbuch.kapitelAbspielen", { title: chapter.title })}
            >
              {isActive && isCurrentlyPlaying ? (
                <Pause className="w-4 h-4" />
              ) : (
                <Play className="w-4 h-4 ml-0.5" />
              )}
            </button>
          )}
        </div>
      </button>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   Purchase Banner
   ═══════════════════════════════════════════════════════════ */
function PurchaseBanner({ onPurchase, isLoading }: { onPurchase: () => void; isLoading: boolean }) {
  const { t } = useI18n();
  return (
    <div className="bg-gradient-to-br from-gold/10 via-olive/5 to-gold/10 border border-gold/20 rounded-2xl p-5 sm:p-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
        <div className="w-14 h-14 rounded-2xl bg-gold/15 flex items-center justify-center shrink-0">
          <Crown className="w-7 h-7 text-gold-dark" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-display font-bold text-foreground">{t("hoerbuch.freischalten")}</h3>
          <p className="text-sm text-muted-foreground mt-1">
            {t("hoerbuch.purchaseDesc", { n: String(CHAPTERS.length), total: String(TOTAL_DURATION) })}
          </p>
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          <span className="text-2xl font-display font-bold text-olive-dark">9,90 €</span>
          <span className="text-xs text-muted-foreground">{t("hoerbuch.einmaligerKauf")}</span>
        </div>
      </div>

      <div className="mt-4 flex flex-col sm:flex-row gap-3">
        <button
          onClick={onPurchase}
          disabled={isLoading}
          className="flex-1 flex items-center justify-center gap-2 bg-olive hover:bg-olive-dark text-ivory font-semibold py-3 px-6 rounded-xl transition-all shadow-md hover:shadow-lg active:scale-[0.98] disabled:opacity-50"
        >
          {isLoading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <ShoppingCart className="w-4 h-4" />
          )}
          {t("hoerbuch.jetztKaufen")}
        </button>
      </div>

      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
        <span className="flex items-center gap-1"><Check className="w-3 h-3 text-olive" /> {t("hoerbuch.sofortVerfuegbar")}</span>
        <span className="flex items-center gap-1"><Check className="w-3 h-3 text-olive" /> {t("hoerbuch.dauerhafterZugang")}</span>
        <span className="flex items-center gap-1"><Check className="w-3 h-3 text-olive" /> {t("hoerbuch.professionell")}</span>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   Offline Download Section
   ═══════════════════════════════════════════════════════════ */
function OfflineDownloadSection() {
  const { t } = useI18n();
  const { cacheAudioFiles, clearCache, isCaching, cachedUrls, isUrlCached } = useAudioCache();
  const allAudioUrls = useMemo(() => CHAPTERS.map((c) => c.audioUrl), []);
  const cachedChapterCount = CHAPTERS.filter((c) => isUrlCached(c.audioUrl)).length;
  const allCached = cachedChapterCount === CHAPTERS.length;

  const handleDownloadAll = () => {
    const uncached = allAudioUrls.filter((url) => !isUrlCached(url));
    if (uncached.length === 0) {
      toast.info(t("hoerbuch.allesBereitsCached"));
      return;
    }
    toast.info(t("hoerbuch.kapitelWerdenGeladen", { n: String(uncached.length) }));
    cacheAudioFiles(uncached);
  };

  const handleClearCache = () => {
    clearCache();
    toast.success(t("hoerbuch.cacheClearedToast"));
  };

  return (
    <div className="bg-card border border-border/50 rounded-2xl p-4 mb-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${
            allCached ? "bg-olive/15 text-olive" : "bg-muted text-muted-foreground"
          }`}>
            {allCached ? <Wifi className="w-4.5 h-4.5" /> : <WifiOff className="w-4.5 h-4.5" />}
          </div>
          <div>
            <h3 className="text-sm font-semibold text-foreground">{t("hoerbuch.offlineModus")}</h3>
            <p className="text-xs text-muted-foreground">
              {allCached
                ? t("hoerbuch.alleOffline")
                : t("hoerbuch.kapitelGespeichert", { n: String(cachedChapterCount), m: String(CHAPTERS.length) })}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {cachedChapterCount > 0 && (
            <button
              onClick={handleClearCache}
              disabled={isCaching}
              className="text-xs text-muted-foreground hover:text-destructive px-2 py-1.5 rounded-lg hover:bg-destructive/5 transition-colors disabled:opacity-50"
            >
              {t("hoerbuch.loeschen")}
            </button>
          )}
          {!allCached && (
            <button
              onClick={handleDownloadAll}
              disabled={isCaching}
              className="flex items-center gap-1.5 text-xs font-medium text-olive bg-olive/10 hover:bg-olive/20 px-3 py-2 rounded-lg transition-colors disabled:opacity-50"
            >
              {isCaching ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Download className="w-3.5 h-3.5" />
              )}
              {isCaching ? t("hoerbuch.laedt") : t("hoerbuch.alleHerunterladen")}
            </button>
          )}
        </div>
      </div>

      {/* Progress bar during caching */}
      {isCaching && (
        <div className="mt-3">
          <div className="h-1.5 bg-muted rounded-full overflow-hidden">
            <div className="h-full bg-olive rounded-full animate-pulse" style={{ width: "60%" }} />
          </div>
          <p className="text-[10px] text-muted-foreground mt-1">{t("hoerbuch.kapitelWerdenGespeichert")}</p>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   Mini Player — fixed at bottom on mobile
   ═══════════════════════════════════════════════════════════ */
function MiniPlayer({
  chapter,
  playbackState,
  onTogglePlay,
  onNext,
  hasNext,
  onClose,
  playbackRate,
  onChangeSpeed,
  sleepTimerRemaining,
}: {
  chapter: Chapter;
  playbackState: PlaybackState;
  onTogglePlay: () => void;
  onNext: () => void;
  hasNext: boolean;
  onClose: () => void;
  playbackRate: number;
  onChangeSpeed: () => void;
  sleepTimerRemaining: number;
}) {
  const progress = playbackState.duration > 0
    ? (playbackState.currentTime / playbackState.duration) * 100
    : 0;

  return (
    <div className="fixed bottom-[4.5rem] left-0 right-0 z-40 sm:hidden">
      <div className="mx-2 bg-midnight/95 backdrop-blur-lg rounded-2xl shadow-2xl border border-olive/20 overflow-hidden">
        {/* Progress bar at top */}
        <div className="h-1 bg-ivory/10">
          <div
            className="h-full bg-gold transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>

        <div className="flex items-center gap-3 px-4 py-3">
          {/* Chapter icon */}
          <div className="w-10 h-10 rounded-xl bg-gold/20 flex items-center justify-center shrink-0">
            <BookAudio className="w-5 h-5 text-gold" />
          </div>

          {/* Chapter info */}
          <div className="min-w-0 flex-1">
            <h4 className="text-sm font-semibold text-ivory truncate">{chapter.title}</h4>
            <p className="text-xs text-ivory/50">
              {formatTime(playbackState.currentTime)} / {playbackState.duration > 0 ? formatTime(playbackState.duration) : `~${chapter.durationMin} Min`}
              {sleepTimerRemaining > 0 && (
                <span className="ml-2 text-gold">
                  <Moon className="w-2.5 h-2.5 inline mr-0.5" />
                  {Math.floor(sleepTimerRemaining / 60)}:{String(sleepTimerRemaining % 60).padStart(2, "0")}
                </span>
              )}
            </p>
          </div>

          {/* Controls */}
          <div className="flex items-center gap-1">
            {/* Speed */}
            <button
              onClick={onChangeSpeed}
              className="w-8 h-8 rounded-full flex items-center justify-center text-ivory/50 hover:text-ivory transition-colors text-[10px] font-bold"
            >
              {playbackRate}x
            </button>

            {/* Play/Pause */}
            <button
              onClick={onTogglePlay}
              className="w-10 h-10 rounded-full bg-gold text-midnight flex items-center justify-center active:scale-90 transition-transform"
            >
              {playbackState.isPlaying ? (
                <Pause className="w-5 h-5" />
              ) : (
                <Play className="w-5 h-5 ml-0.5" />
              )}
            </button>

            {/* Next */}
            {hasNext && (
              <button
                onClick={onNext}
                className="w-9 h-9 rounded-full flex items-center justify-center text-ivory/60 hover:text-ivory transition-colors"
              >
                <SkipForward className="w-4 h-4" />
              </button>
            )}

            {/* Close */}
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-full flex items-center justify-center text-ivory/40 hover:text-ivory/70 transition-colors"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   Main Component
   ═══════════════════════════════════════════════════════════ */
export default function AppHoerbuch() {
  const { client } = useClientAuth();
  const { isDemo } = useDemo();
  const { t } = useI18n();
  const [activeChapterId, setActiveChapterId] = useState(CHAPTERS[0].id);
  const [showAllChapters, setShowAllChapters] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [shouldAutoPlay, setShouldAutoPlay] = useState(false);
  const [miniPlayerDismissed, setMiniPlayerDismissed] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const playerRef = useRef<HTMLDivElement>(null);

  // ─── Sleep Timer ─────────────────────────────────────────
  const [sleepTimerMinutes, setSleepTimerMinutes] = useState<number | null>(null);
  const [sleepTimerRemaining, setSleepTimerRemaining] = useState<number>(0);
  const sleepTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Start / stop sleep timer
  const startSleepTimer = useCallback((minutes: number) => {
    if (sleepTimerRef.current) clearInterval(sleepTimerRef.current);
    setSleepTimerMinutes(minutes);
    setSleepTimerRemaining(minutes * 60);
    sleepTimerRef.current = setInterval(() => {
      setSleepTimerRemaining((prev) => {
        if (prev <= 1) {
          // Time's up — pause playback
          window.dispatchEvent(new CustomEvent("ayu-sleep-timer-stop"));
          clearInterval(sleepTimerRef.current!);
          sleepTimerRef.current = null;
          setSleepTimerMinutes(null);
          toast.info(t("hoerbuch.sleepTimerAbgelaufen"));
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  const cancelSleepTimer = useCallback(() => {
    if (sleepTimerRef.current) clearInterval(sleepTimerRef.current);
    sleepTimerRef.current = null;
    setSleepTimerMinutes(null);
    setSleepTimerRemaining(0);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (sleepTimerRef.current) clearInterval(sleepTimerRef.current);
    };
  }, []);

  // ─── Notes state ─────────────────────────────────────────
  const [showNotes, setShowNotes] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [editingNoteId, setEditingNoteId] = useState<number | null>(null);
  const [editNoteText, setEditNoteText] = useState("");

  // ─── KI-Zusammenfassungen ────────────────────────────────
  const [showSummary, setShowSummary] = useState(false);
  const { data: summaries, refetch: refetchSummaries } = trpc.stripe.getSummaries.useQuery(
    { clientId: client?.clientId ?? "" },
    { enabled: !!client?.clientId }
  );

  const generateSummaryMutation = trpc.stripe.generateSummary.useMutation({
    onSuccess: () => {
      toast.success(t("hoerbuch.zusammenfassungErstellt"));
      refetchSummaries();
    },
    onError: (err) => {
      toast.error(err.message || t("hoerbuch.zusammenfassungFehler"));
    },
  });

  const currentSummary = summaries?.find((s) => s.chapterId === activeChapterId);

  const handleGenerateSummary = () => {
    if (!client?.clientId) return;
    generateSummaryMutation.mutate({
      clientId: client.clientId,
      chapterId: activeChapterId,
      chapterTitle: activeChapter.title,
      chapterDescription: activeChapter.description,
    });
  };

  // ─── Hörbuch-Statistiken ─────────────────────────────────
  const [showStats, setShowStats] = useState(false);
  const { data: audiobookStats } = trpc.stripe.getStats.useQuery(
    { clientId: client?.clientId ?? "" },
    { enabled: !!client?.clientId }
  );

  // ─── PDF Export ──────────────────────────────────────────
  const { data: exportData } = trpc.stripe.getNotesForExport.useQuery(
    { clientId: client?.clientId ?? "" },
    { enabled: !!client?.clientId }
  );

  const handleExportPdf = () => {
    if (!exportData) return;
    const { notes: expNotes, bookmarks: expBookmarks, summaries: expSummaries } = exportData;
    if (expNotes.length === 0 && expBookmarks.length === 0 && expSummaries.length === 0) {
      toast.info(t("hoerbuch.nochKeineExportDaten"));
      return;
    }

    // Build markdown content
    let md = `# AYU Hörbuch — Meine Notizen\n\n`;
    md += `**Exportiert am:** ${new Date().toLocaleDateString("de-DE", { day: "2-digit", month: "long", year: "numeric" })}\n\n`;

    // Summaries
    if (expSummaries.length > 0) {
      md += `## KI-Zusammenfassungen\n\n`;
      for (const s of expSummaries) {
        const ch = CHAPTERS.find((c) => c.id === s.chapterId);
        md += `### ${ch?.title || s.chapterId}\n\n`;
        md += `${s.summary}\n\n`;
        if (s.keyPoints && s.keyPoints.length > 0) {
          md += `**Kernpunkte:**\n`;
          for (const kp of s.keyPoints) {
            md += `- ${kp}\n`;
          }
          md += `\n`;
        }
      }
    }

    // Notes
    if (expNotes.length > 0) {
      md += `## Meine Notizen\n\n`;
      for (const n of expNotes) {
        const ch = CHAPTERS.find((c) => c.id === n.chapterId);
        const time = formatTime(n.timeSec);
        md += `- **${ch?.title || n.chapterId}** (${time}): ${n.content}\n`;
      }
      md += `\n`;
    }

    // Bookmarks
    if (expBookmarks.length > 0) {
      md += `## Lesezeichen\n\n`;
      for (const b of expBookmarks) {
        const ch = CHAPTERS.find((c) => c.id === b.chapterId);
        md += `- ${ch?.title || b.chapterId} — ${formatTime(b.timeSec)}${b.label ? ` (${b.label})` : ""}\n`;
      }
    }

    // Download as text file
    const blob = new Blob([md], { type: "text/markdown;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `AYU_Hoerbuch_Notizen_${new Date().toISOString().split("T")[0]}.md`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(t("hoerbuch.notizenExportiert"));
  };

  // Playback state shared from AudiobookPlayer
  const [playbackState, setPlaybackState] = useState<PlaybackState>({
    isPlaying: false,
    currentTime: 0,
    duration: 0,
  });

  // Track progress per chapter (chapterId → percent)
  const [chapterProgress, setChapterProgress] = useState<Record<string, number>>({});

  // Bookmarks state
  const [showBookmarks, setShowBookmarks] = useState(false);

  // ─── Server-side progress persistence ────────────────────
  const { data: serverProgress } = trpc.stripe.getAudiobookProgress.useQuery(
    { clientId: client?.clientId ?? "" },
    { enabled: !!client?.clientId }
  );

  // Load server progress into local state on mount
  useEffect(() => {
    if (serverProgress && serverProgress.length > 0) {
      const map: Record<string, number> = {};
      for (const p of serverProgress) {
        map[p.chapterId] = p.progressPercent;
      }
      setChapterProgress((prev) => {
        const merged = { ...prev };
        for (const [k, v] of Object.entries(map)) {
          merged[k] = Math.max(merged[k] || 0, v);
        }
        return merged;
      });
    }
  }, [serverProgress]);

  // Save progress mutation
  const saveProgressMutation = trpc.stripe.saveProgress.useMutation();

  // Debounced save to server (every 10 seconds while playing)
  const lastSaveRef = useRef(0);
  useEffect(() => {
    if (!client?.clientId || !playbackState.isPlaying || playbackState.duration <= 0) return;
    const now = Date.now();
    if (now - lastSaveRef.current < 10000) return;
    lastSaveRef.current = now;
    const pct = Math.round((playbackState.currentTime / playbackState.duration) * 100);
    saveProgressMutation.mutate({
      clientId: client.clientId,
      chapterId: activeChapterId,
      progressPercent: pct,
      currentTimeSec: Math.round(playbackState.currentTime),
      durationSec: Math.round(playbackState.duration),
    });
  }, [playbackState.currentTime, playbackState.isPlaying, client?.clientId, activeChapterId]);

  // Also save on pause / chapter change
  const prevPlayingRef = useRef(false);
  useEffect(() => {
    if (prevPlayingRef.current && !playbackState.isPlaying && client?.clientId && playbackState.duration > 0) {
      const pct = Math.round((playbackState.currentTime / playbackState.duration) * 100);
      saveProgressMutation.mutate({
        clientId: client.clientId,
        chapterId: activeChapterId,
        progressPercent: pct,
        currentTimeSec: Math.round(playbackState.currentTime),
        durationSec: Math.round(playbackState.duration),
      });
    }
    prevPlayingRef.current = playbackState.isPlaying;
  }, [playbackState.isPlaying]);

  // ─── Bookmarks ───────────────────────────────────────────
  const { data: bookmarks, refetch: refetchBookmarks } = trpc.stripe.getBookmarks.useQuery(
    { clientId: client?.clientId ?? "" },
    { enabled: !!client?.clientId }
  );

  const createBookmarkMutation = trpc.stripe.createBookmark.useMutation({
    onSuccess: () => {
      toast.success(t("hoerbuch.lesezeichenGespeichert"));
      refetchBookmarks();
    },
  });

  const deleteBookmarkMutation = trpc.stripe.deleteBookmark.useMutation({
    onSuccess: () => {
      toast.success(t("hoerbuch.lesezeichenGeloescht"));
      refetchBookmarks();
    },
  });

  const handleAddBookmark = () => {
    if (!client?.clientId || playbackState.duration <= 0) return;
    const timeSec = Math.round(playbackState.currentTime);
    const label = `${activeChapter.title} — ${formatTime(timeSec)}`;
    createBookmarkMutation.mutate({
      clientId: client.clientId,
      chapterId: activeChapterId,
      timeSec,
      label,
    });
  };

  // ─── Notes ─────────────────────────────────────────────────
  const { data: notes, refetch: refetchNotes } = trpc.stripe.getNotes.useQuery(
    { clientId: client?.clientId ?? "" },
    { enabled: !!client?.clientId }
  );

  const createNoteMutation = trpc.stripe.createNote.useMutation({
    onSuccess: () => {
      toast.success(t("hoerbuch.notizGespeichert"));
      refetchNotes();
      setNoteText("");
    },
  });

  const updateNoteMutation = trpc.stripe.updateNote.useMutation({
    onSuccess: () => {
      toast.success(t("hoerbuch.notizAktualisiert"));
      refetchNotes();
      setEditingNoteId(null);
      setEditNoteText("");
    },
  });

  const deleteNoteMutation = trpc.stripe.deleteNote.useMutation({
    onSuccess: () => {
      toast.success(t("hoerbuch.notizGeloescht"));
      refetchNotes();
    },
  });

  const handleAddNote = () => {
    if (!client?.clientId || !noteText.trim() || playbackState.duration <= 0) return;
    createNoteMutation.mutate({
      clientId: client.clientId,
      chapterId: activeChapterId,
      timeSec: Math.round(playbackState.currentTime),
      content: noteText.trim(),
    });
  };

  const handleJumpToBookmark = (chapterId: string, timeSec: number) => {
    const chapter = CHAPTERS.find((c) => c.id === chapterId);
    if (!chapter || !canPlayChapter(chapter)) return;
    setActiveChapterId(chapterId);
    setShouldAutoPlay(true);
    setMiniPlayerDismissed(false);
    // We need to seek after the audio loads — use a custom event
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent("ayu-seek-to", { detail: { timeSec } }));
    }, 500);
  };

  // Update chapter progress when playback state changes
  useEffect(() => {
    if (playbackState.duration > 0) {
      const pct = (playbackState.currentTime / playbackState.duration) * 100;
      setChapterProgress((prev) => ({
        ...prev,
        [activeChapterId]: Math.max(prev[activeChapterId] || 0, pct),
      }));
    }
  }, [playbackState.currentTime, playbackState.duration, activeChapterId]);

  // Detect if main player is scrolled out of view (for mini-player)
  const [playerOutOfView, setPlayerOutOfView] = useState(false);
  useEffect(() => {
    const el = playerRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setPlayerOutOfView(!entry.isIntersecting),
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [playerRef.current]);

  // Check if user has purchased the audiobook
  const { data: accessData, isLoading: accessLoading } = trpc.stripe.getAudiobookAccess.useQuery(
    { clientId: client?.clientId ?? "" },
    { enabled: !!client?.clientId }
  );

  // Also check premium status — premium users get audiobook for free
  const { data: premiumData } = trpc.stripe.getPremiumStatus.useQuery(
    { clientId: client?.clientId ?? "" },
    { enabled: !!client?.clientId }
  );

  const hasPurchased = accessData?.hasPurchased || false;
  const isPremium = premiumData?.hasAccess || false;
  const hasFullAccess = hasPurchased || isPremium || isDemo;

  // Offline cache status
  const { isUrlCached } = useAudioCache();

  const activeChapter = CHAPTERS.find((c) => c.id === activeChapterId) || CHAPTERS[0];
  const activeIndex = CHAPTERS.findIndex((c) => c.id === activeChapterId);

  // Checkout
  const checkoutMutation = trpc.stripe.createAudiobookCheckout.useMutation({
    onSuccess: (data) => {
      if (data.url) {
        toast.info(t("hoerbuch.weiterleitungKasse"));
        window.open(data.url, "_blank");
      }
      setCheckoutLoading(false);
    },
    onError: (err) => {
      toast.error(err.message || t("hoerbuch.checkoutFehler"));
      setCheckoutLoading(false);
    },
  });

  const handlePurchase = () => {
    if (!client) {
      toast.error(t("hoerbuch.bitteMelden"));
      return;
    }
    setCheckoutLoading(true);
    checkoutMutation.mutate({ origin: window.location.origin });
  };

  // Handle checkout success from URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("checkout") === "success") {
      toast.success(t("hoerbuch.erfolgreichGekauft"));
      window.history.replaceState({}, "", window.location.pathname);
    } else if (params.get("checkout") === "cancel") {
      toast.info(t("hoerbuch.kaufAbgebrochen"));
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const canPlayChapter = (chapter: Chapter) => chapter.isFree || hasFullAccess;

  const handleSelectChapter = (chapter: Chapter, autoPlay = false) => {
    if (!canPlayChapter(chapter)) {
      toast(t("hoerbuch.nurVollversion"), {
        description: t("hoerbuch.vollversionBeschreibung"),
        action: { label: t("hoerbuch.kaufenAction"), onClick: handlePurchase },
      });
      return;
    }
    if (autoPlay) {
      setShouldAutoPlay(true);
      setMiniPlayerDismissed(false);
    }
    setActiveChapterId(chapter.id);
    // Scroll to player on mobile so user sees it
    setTimeout(() => {
      playerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  };

  // Toggle play/pause from mini-player or chapter list
  const handleTogglePlayFromExternal = useCallback(() => {
    // We dispatch a custom event that the AudiobookPlayer listens to
    window.dispatchEvent(new CustomEvent("ayu-toggle-play"));
  }, []);

  // Change speed from mini-player (cycles through 0.75, 1, 1.25, 1.5, 1.75, 2)
  const handleChangeSpeed = useCallback(() => {
    setPlaybackRate((r) => {
      const next = r >= 2 ? 0.75 : r + 0.25;
      window.dispatchEvent(new CustomEvent("ayu-set-speed", { detail: { rate: next } }));
      return next;
    });
  }, []);

  const goToNext = () => {
    const nextIndex = activeIndex + 1;
    if (nextIndex < CHAPTERS.length && canPlayChapter(CHAPTERS[nextIndex])) {
      setActiveChapterId(CHAPTERS[nextIndex].id);
    }
  };

  const goToPrev = () => {
    const prevIndex = activeIndex - 1;
    if (prevIndex >= 0) {
      setActiveChapterId(CHAPTERS[prevIndex].id);
    }
  };

  const visibleChapters = showAllChapters ? CHAPTERS : CHAPTERS.slice(0, 5);

  const showMiniPlayer = playbackState.isPlaying && playerOutOfView && !miniPlayerDismissed;

  if (accessLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-olive" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 pb-32 sm:pb-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-2 text-xs text-gold font-medium tracking-wider uppercase mb-2">
          <BookAudio className="w-4 h-4" />
          <span>{t("hoerbuch.title")}</span>
        </div>
        <h1 className="text-2xl sm:text-3xl font-display font-bold text-foreground leading-tight">
          Der AYU – Tradition trifft Genetik
        </h1>
        <p className="text-sm text-muted-foreground mt-2 font-body">
          {t("hoerbuch.buchMeta", { n: String(CHAPTERS.length), total: String(TOTAL_DURATION) })}
        </p>

        {hasFullAccess && (
          <div className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-olive bg-olive/10 px-3 py-1.5 rounded-full">
            <Check className="w-3.5 h-3.5" />
            {isPremium && !hasPurchased ? t("hoerbuch.premiumZugang") : t("hoerbuch.freigeschaltet")}
          </div>
        )}
      </div>

      {/* Player — only show if active chapter is playable */}
      {canPlayChapter(activeChapter) && (
        <div className="mb-6" ref={playerRef}>
          <AudiobookPlayer
            chapter={activeChapter}
            onNext={goToNext}
            onPrev={goToPrev}
            hasNext={activeIndex < CHAPTERS.length - 1 && canPlayChapter(CHAPTERS[activeIndex + 1])}
            hasPrev={activeIndex > 0}
            autoPlay={shouldAutoPlay}
            onAutoPlayHandled={() => setShouldAutoPlay(false)}
            onPlaybackChange={setPlaybackState}
          />
        </div>
      )}

      {/* Action buttons: Bookmark, Notes, Sleep Timer */}
      {canPlayChapter(activeChapter) && client && playbackState.duration > 0 && (
        <div className="flex items-center gap-2 mb-4 -mt-2 flex-wrap">
          <button
            onClick={handleAddBookmark}
            disabled={createBookmarkMutation.isPending}
            className="flex items-center gap-1.5 text-xs font-medium text-olive hover:text-olive-dark bg-olive/5 hover:bg-olive/10 px-3 py-2 rounded-lg transition-all"
          >
            <Bookmark className="w-3.5 h-3.5" />
            {t("hoerbuch.lesezeichen")}
          </button>
          {bookmarks && bookmarks.length > 0 && (
            <button
              onClick={() => setShowBookmarks(!showBookmarks)}
              className="flex items-center gap-1.5 text-xs font-medium text-gold hover:text-gold-dark bg-gold/5 hover:bg-gold/10 px-3 py-2 rounded-lg transition-all"
            >
              <BookmarkCheck className="w-3.5 h-3.5" />
              {bookmarks.length}
              {showBookmarks ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </button>
          )}

          {/* Notes toggle */}
          <button
            onClick={() => setShowNotes(!showNotes)}
            className={`flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-lg transition-all ${
              showNotes
                ? "text-olive bg-olive/15"
                : "text-muted-foreground hover:text-olive bg-muted/50 hover:bg-olive/5"
            }`}
          >
            <StickyNote className="w-3.5 h-3.5" />
            {t("hoerbuch.notizen")}{notes && notes.length > 0 ? ` (${notes.length})` : ""}
          </button>

          {/* KI-Zusammenfassung */}
          <button
            onClick={() => setShowSummary(!showSummary)}
            className={`flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-lg transition-all ${
              showSummary
                ? "text-gold bg-gold/15"
                : "text-muted-foreground hover:text-gold bg-muted/50 hover:bg-gold/5"
            }`}
          >
            <Brain className="w-3.5 h-3.5" />
            {t("hoerbuch.kiSummary")}
          </button>

          {/* Statistiken */}
          <button
            onClick={() => setShowStats(!showStats)}
            className={`flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-lg transition-all ${
              showStats
                ? "text-olive bg-olive/15"
                : "text-muted-foreground hover:text-olive bg-muted/50 hover:bg-olive/5"
            }`}
          >
            <BarChart3 className="w-3.5 h-3.5" />
            {t("hoerbuch.statistik")}
          </button>

          {/* PDF Export */}
          <button
            onClick={handleExportPdf}
            className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground bg-muted/50 hover:bg-muted px-3 py-2 rounded-lg transition-all"
          >
            <FileDown className="w-3.5 h-3.5" />
            {t("hoerbuch.export")}
          </button>

          {/* Sleep Timer */}
          <div className="relative group">
            <button
              className={`flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-lg transition-all ${
                sleepTimerMinutes
                  ? "text-gold bg-gold/10"
                  : "text-muted-foreground hover:text-olive bg-muted/50 hover:bg-olive/5"
              }`}
            >
              <Moon className="w-3.5 h-3.5" />
              {sleepTimerMinutes
                ? `${Math.floor(sleepTimerRemaining / 60)}:${String(sleepTimerRemaining % 60).padStart(2, "0")}`
                : t("hoerbuch.sleep")}
            </button>
            {/* Dropdown */}
            <div className="absolute top-full left-0 mt-1 bg-card border border-border rounded-xl shadow-lg p-2 hidden group-hover:block z-50 min-w-[140px]">
              {sleepTimerMinutes ? (
                <button
                  onClick={cancelSleepTimer}
                  className="w-full text-left text-xs px-3 py-2 rounded-lg hover:bg-destructive/10 text-destructive transition-colors"
                >
                  {t("hoerbuch.timerAbbrechen")}
                </button>
              ) : (
                [15, 30, 45, 60, 90].map((min) => (
                  <button
                    key={min}
                    onClick={() => {
                      startSleepTimer(min);
                      toast.success(t("hoerbuch.sleepTimerGesetzt", { n: String(min) }));
                    }}
                    className="w-full text-left text-xs px-3 py-2 rounded-lg hover:bg-olive/10 text-foreground transition-colors"
                  >
                    {t("hoerbuch.minuten", { n: String(min) })}
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Bookmarks Panel */}
      {showBookmarks && bookmarks && bookmarks.length > 0 && (
        <div className="bg-card border border-gold/20 rounded-xl p-4 mb-6">
          <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
            <BookmarkCheck className="w-4 h-4 text-gold" />
            {t("hoerbuch.deineLesezeichen")}
          </h3>
          <div className="space-y-2">
            {bookmarks.map((bm) => {
              const ch = CHAPTERS.find((c) => c.id === bm.chapterId);
              return (
                <div
                  key={bm.id}
                  className="flex items-center gap-3 p-2.5 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors group"
                >
                  <button
                    onClick={() => handleJumpToBookmark(bm.chapterId, bm.timeSec)}
                    className="flex-1 min-w-0 text-left"
                  >
                    <p className="text-sm font-medium text-foreground truncate">
                      {bm.label || `${ch?.title || bm.chapterId} — ${formatTime(bm.timeSec)}`}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {ch?.title || bm.chapterId} · {formatTime(bm.timeSec)}
                    </p>
                  </button>
                  <button
                    onClick={() => client && deleteBookmarkMutation.mutate({ id: bm.id, clientId: client.clientId })}
                    className="w-7 h-7 rounded-full flex items-center justify-center text-muted-foreground/50 hover:text-red-500 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-all"
                    title={t("hoerbuch.lesezeichenLoeschen")}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Notes Panel */}
      {showNotes && (
        <div className="bg-card border border-border/50 rounded-xl p-4 mb-6">
          <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
            <StickyNote className="w-4 h-4 text-olive" />
            {t("hoerbuch.deineNotizen")}
          </h3>

          {/* Add new note */}
          {playbackState.duration > 0 && (
            <div className="mb-4">
              <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1.5">
                <Timer className="w-3 h-3" />
                <span>{activeChapter.title} — {formatTime(Math.round(playbackState.currentTime))}</span>
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleAddNote()}
                  placeholder={t("hoerbuch.notizEingeben")}
                  className="flex-1 text-sm bg-muted/50 border border-border/50 rounded-lg px-3 py-2 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-olive/30"
                />
                <button
                  onClick={handleAddNote}
                  disabled={!noteText.trim() || createNoteMutation.isPending}
                  className="px-3 py-2 text-xs font-medium bg-olive text-ivory rounded-lg hover:bg-olive-dark transition-colors disabled:opacity-50"
                >
                  {t("hoerbuch.speichern")}
                </button>
              </div>
            </div>
          )}

          {/* Existing notes */}
          {notes && notes.length > 0 ? (
            <div className="space-y-2">
              {notes.map((note) => {
                const ch = CHAPTERS.find((c) => c.id === note.chapterId);
                const isEditing = editingNoteId === note.id;
                return (
                  <div
                    key={note.id}
                    className="p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors group"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <button
                          onClick={() => handleJumpToBookmark(note.chapterId, note.timeSec)}
                          className="text-xs text-olive hover:text-olive-dark font-medium flex items-center gap-1 mb-1"
                        >
                          <Clock className="w-3 h-3" />
                          {ch?.title || note.chapterId} — {formatTime(note.timeSec)}
                        </button>
                        {isEditing ? (
                          <div className="flex gap-2 mt-1">
                            <input
                              type="text"
                              value={editNoteText}
                              onChange={(e) => setEditNoteText(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" && editNoteText.trim()) {
                                  updateNoteMutation.mutate({
                                    id: note.id,
                                    clientId: client!.clientId,
                                    content: editNoteText.trim(),
                                  });
                                }
                                if (e.key === "Escape") {
                                  setEditingNoteId(null);
                                }
                              }}
                              className="flex-1 text-sm bg-background border border-border rounded-lg px-2 py-1 text-foreground focus:outline-none focus:ring-1 focus:ring-olive/30"
                              autoFocus
                            />
                            <button
                              onClick={() => {
                                if (editNoteText.trim()) {
                                  updateNoteMutation.mutate({
                                    id: note.id,
                                    clientId: client!.clientId,
                                    content: editNoteText.trim(),
                                  });
                                }
                              }}
                              className="text-xs text-olive hover:text-olive-dark px-2 py-1 rounded-lg bg-olive/10"
                            >
                              OK
                            </button>
                          </div>
                        ) : (
                          <p className="text-sm text-foreground">{note.content}</p>
                        )}
                      </div>
                      {!isEditing && (
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                          <button
                            onClick={() => {
                              setEditingNoteId(note.id);
                              setEditNoteText(note.content);
                            }}
                            className="w-6 h-6 rounded-full flex items-center justify-center text-muted-foreground/50 hover:text-olive hover:bg-olive/10 transition-all"
                            title={t("hoerbuch.bearbeiten")}
                          >
                            <Pencil className="w-3 h-3" />
                          </button>
                          <button
                            onClick={() => client && deleteNoteMutation.mutate({ id: note.id, clientId: client.clientId })}
                            className="w-6 h-6 rounded-full flex items-center justify-center text-muted-foreground/50 hover:text-red-500 hover:bg-red-50 transition-all"
                            title={t("hoerbuch.loeschen")}
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground text-center py-3">
              {t("hoerbuch.keineNotizenHinweis")}
            </p>
          )}
        </div>
      )}

      {/* KI-Zusammenfassung Panel */}
      {showSummary && client && (
        <div className="bg-card border border-gold/20 rounded-xl p-4 mb-6">
          <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
            <Brain className="w-4 h-4 text-gold" />
            {t("hoerbuch.kiZusammenfassung")} — {activeChapter.title}
          </h3>

          {currentSummary ? (
            <div>
              <p className="text-sm text-foreground leading-relaxed mb-3">{currentSummary.summary}</p>
              {currentSummary.keyPoints && currentSummary.keyPoints.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-muted-foreground mb-1.5 flex items-center gap-1">
                    <Lightbulb className="w-3 h-3" />
                    {t("hoerbuch.kernpunkte")}
                  </p>
                  <ul className="space-y-1">
                    {currentSummary.keyPoints.map((kp: string, i: number) => (
                      <li key={i} className="text-sm text-foreground flex items-start gap-2">
                        <span className="w-5 h-5 rounded-full bg-gold/10 text-gold text-[10px] font-bold flex items-center justify-center shrink-0 mt-0.5">{i + 1}</span>
                        {kp}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-4">
              <p className="text-xs text-muted-foreground mb-3">
                {t("hoerbuch.kiHinweis")}
              </p>
              <button
                onClick={handleGenerateSummary}
                disabled={generateSummaryMutation.isPending}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium bg-gold text-ivory rounded-lg hover:bg-gold-dark transition-colors disabled:opacity-50"
              >
                {generateSummaryMutation.isPending ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> {t("hoerbuch.wirdErstellt")}</>
                ) : (
                  <><Brain className="w-4 h-4" /> {t("hoerbuch.zusammenfassungGenerieren")}</>
                )}
              </button>
            </div>
          )}

          {/* Show count of all summaries */}
          {summaries && summaries.length > 0 && (
            <div className="mt-3 pt-3 border-t border-border/30">
              <p className="text-[10px] text-muted-foreground">
                {t("hoerbuch.summariesVon", { n: String(summaries.length), m: String(CHAPTERS.length) })}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Statistiken Panel */}
      {showStats && audiobookStats && (
        <div className="bg-card border border-olive/20 rounded-xl p-4 mb-6">
          <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-olive" />
            {t("hoerbuch.hoerbuchStatistiken")}
          </h3>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {/* Total listening time */}
            <div className="bg-muted/30 rounded-lg p-3 text-center">
              <div className="w-8 h-8 rounded-full bg-olive/10 flex items-center justify-center mx-auto mb-1.5">
                <Clock className="w-4 h-4 text-olive" />
              </div>
              <p className="text-lg font-bold text-foreground">
                {Math.floor(audiobookStats.totalListeningTimeSec / 3600) > 0
                  ? `${Math.floor(audiobookStats.totalListeningTimeSec / 3600)}h ${Math.floor((audiobookStats.totalListeningTimeSec % 3600) / 60)}m`
                  : `${Math.floor(audiobookStats.totalListeningTimeSec / 60)}m`}
              </p>
              <p className="text-[10px] text-muted-foreground">{t("hoerbuch.gehoertLabel")}</p>
            </div>

            {/* Chapters completed */}
            <div className="bg-muted/30 rounded-lg p-3 text-center">
              <div className="w-8 h-8 rounded-full bg-gold/10 flex items-center justify-center mx-auto mb-1.5">
                <Check className="w-4 h-4 text-gold" />
              </div>
              <p className="text-lg font-bold text-foreground">
                {audiobookStats.chaptersCompleted}/{audiobookStats.totalChapters}
              </p>
              <p className="text-[10px] text-muted-foreground">{t("hoerbuch.abgeschlossen")}</p>
            </div>

            {/* Average progress */}
            <div className="bg-muted/30 rounded-lg p-3 text-center">
              <div className="w-8 h-8 rounded-full bg-olive/10 flex items-center justify-center mx-auto mb-1.5">
                <BarChart3 className="w-4 h-4 text-olive" />
              </div>
              <p className="text-lg font-bold text-foreground">{audiobookStats.avgProgress}%</p>
              <p className="text-[10px] text-muted-foreground">{t("hoerbuch.avFortschritt")}</p>
            </div>

            {/* Listening days */}
            <div className="bg-muted/30 rounded-lg p-3 text-center">
              <div className="w-8 h-8 rounded-full bg-gold/10 flex items-center justify-center mx-auto mb-1.5">
                <Award className="w-4 h-4 text-gold" />
              </div>
              <p className="text-lg font-bold text-foreground">{audiobookStats.listeningDays}</p>
              <p className="text-[10px] text-muted-foreground">{t("hoerbuch.hoerTage")}</p>
            </div>
          </div>

          {/* Most listened chapter */}
          {audiobookStats.mostListenedChapterId && (
            <div className="mt-3 pt-3 border-t border-border/30 flex items-center gap-2">
              <Crown className="w-3.5 h-3.5 text-gold" />
              <p className="text-xs text-muted-foreground">
                {t("hoerbuch.meistGehoert")} <span className="font-medium text-foreground">
                  {CHAPTERS.find(c => c.id === audiobookStats.mostListenedChapterId)?.title || audiobookStats.mostListenedChapterId}
                </span>
                {" "}({formatTime(audiobookStats.mostListenedTimeSec)})
              </p>
            </div>
          )}
        </div>
      )}

      {showStats && !audiobookStats && (
        <div className="bg-card border border-olive/20 rounded-xl p-4 mb-6 text-center">
          <p className="text-xs text-muted-foreground py-3">
            {t("hoerbuch.nochKeineStatistiken")}
          </p>
        </div>
      )}

      {/* Purchase Banner — show if not purchased */}
      {!hasFullAccess && (
        <div className="mb-6">
          <PurchaseBanner onPurchase={handlePurchase} isLoading={checkoutLoading} />
        </div>
      )}

      {/* Offline Download Section */}
      {hasFullAccess && <OfflineDownloadSection />}

      {/* Book Info Card */}
      <div className="bg-card border border-border/50 rounded-2xl p-5 mb-6">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-gold/10 flex items-center justify-center shrink-0">
            <BookOpen className="w-5 h-5 text-gold-dark" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-foreground">{t("hoerbuch.ueberBuch")}</h3>
            <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
              {t("hoerbuch.ueberBuchDesc")}
            </p>
          </div>
        </div>
      </div>

      {/* Chapter List */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-display font-bold text-foreground">{t("hoerbuch.kapitelLabel")}</h2>
          <span className="text-xs text-muted-foreground">
            {hasFullAccess
              ? t("hoerbuch.nKapitel", { n: String(CHAPTERS.length) })
              : t("hoerbuch.nVonMGratis", { n: String(CHAPTERS.filter(c => c.isFree).length), m: String(CHAPTERS.length) })}
          </span>
        </div>

        <div className="space-y-2">
          {visibleChapters.map((chapter) => (
            <ChapterItem
              key={chapter.id}
              chapter={chapter}
              isActive={chapter.id === activeChapterId}
              isLocked={!canPlayChapter(chapter)}
              isCurrentlyPlaying={chapter.id === activeChapterId && playbackState.isPlaying}
              progressPercent={chapterProgress[chapter.id] || 0}
              isOffline={isUrlCached(chapter.audioUrl)}
              onSelect={() => handleSelectChapter(chapter)}
              onPlay={() => handleSelectChapter(chapter, true)}
              onPause={handleTogglePlayFromExternal}
            />
          ))}
        </div>

        {CHAPTERS.length > 5 && (
          <button
            onClick={() => setShowAllChapters(!showAllChapters)}
            className="w-full mt-3 flex items-center justify-center gap-2 text-sm text-olive font-medium py-2.5 rounded-xl hover:bg-olive/5 transition-colors"
          >
            {showAllChapters ? (
              <>
                <ChevronUp className="w-4 h-4" />
                {t("hoerbuch.wenigerAnzeigen")}
              </>
            ) : (
              <>
                <ChevronDown className="w-4 h-4" />
                {t("hoerbuch.alleKapitelAnzeigen", { n: String(CHAPTERS.length) })}
              </>
            )}
          </button>
        )}
      </div>

      {/* Features */}
      {!hasFullAccess && (
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3">
          {[
            { icon: <Sparkles className="w-5 h-5 text-gold" />, title: t("hoerbuch.featureProfessionell"), desc: t("hoerbuch.featureProfessionellDesc") },
            { icon: <BookAudio className="w-5 h-5 text-olive" />, title: t("hoerbuch.nKapitel", { n: String(CHAPTERS.length) }), desc: t("hoerbuch.featureKapitelDesc", { total: String(TOTAL_DURATION) }) },
            { icon: <Crown className="w-5 h-5 text-gold-dark" />, title: t("hoerbuch.featureDauerhaft"), desc: t("hoerbuch.featureDauerhaftDesc") },
          ].map((f, i) => (
            <div key={i} className="bg-card border border-border/50 rounded-xl p-4 text-center">
              <div className="w-10 h-10 rounded-xl bg-muted flex items-center justify-center mx-auto mb-2">
                {f.icon}
              </div>
              <h4 className="text-sm font-semibold text-foreground">{f.title}</h4>
              <p className="text-xs text-muted-foreground mt-0.5">{f.desc}</p>
            </div>
          ))}
        </div>
      )}

      {/* Mini Player — fixed at bottom on mobile when scrolled past main player */}
      {showMiniPlayer && canPlayChapter(activeChapter) && (
        <MiniPlayer
          chapter={activeChapter}
          playbackState={playbackState}
          onTogglePlay={handleTogglePlayFromExternal}
          onNext={goToNext}
          hasNext={activeIndex < CHAPTERS.length - 1 && canPlayChapter(CHAPTERS[activeIndex + 1])}
          onClose={() => setMiniPlayerDismissed(true)}
          playbackRate={playbackRate}
          onChangeSpeed={handleChangeSpeed}
          sleepTimerRemaining={sleepTimerRemaining}
        />
      )}
    </div>
  );
}
